import type { QueryClient } from "@tanstack/solid-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import type { Accessor } from "solid-js";
import { axiosInstance, queryClient } from "@/api/client";
import {
  AUTH_KEY,
  BILLING_STATUS_KEY,
  FREELANCE_DASHBOARD_KEY,
  PROFILE_KEY,
} from "@/api/query-keys";
import { isClientError } from "@/api/utils/error";
import type { JobApplication } from "@/domains/application/types";
import { fetchSessionBootstrap } from "@/domains/auth/session/api";
import type {
  SessionBootstrapCapabilities,
  SessionBootstrapResponse,
  SessionNextStep,
} from "@/domains/auth/session/types";
import type { CompanyV2 } from "@/domains/company/company/api/types";
import { boundSkillsCsv } from "@/libs/skill-input";
import { tryCatch } from "@/libs/try-catch";
import type { Asset } from "@/types";
import type {
  EducationInsertV2,
  EducationV2,
  OwnProfileV2,
  PreviousJobInsertV2,
  PreviousJobV2,
  ProfileV2,
  PublicProfileV2,
  UpdateEducation,
  UpdateJobExperience,
  UpdateProfileAvailabilityPayload,
  UpdateProfileImagePayload,
  UpdateProfileV2,
  UpdateProfileV2Response,
} from "./types";

/** D6: il salvataggio ha 10 s, la verifica 5 s. */
export const USERNAME_SAVE_TIMEOUT_MS = 10_000;
export const USERNAME_CHECK_TIMEOUT_MS = 5_000;

/**
 * Il tetto della **rilettura di recupero** del profilo (D6): quando il
 * salvataggio scade senza risposta, si ricarica `/me` per sapere se era passato
 * lo stesso. È l'unica strada che riporta la card fuori dallo stato «sto
 * salvando», dove scrim, Escape, X e Annulla tacciono per non interrompere un
 * PATCH in volo — quindi è la strada che **non può** essere più lenta di quella
 * che sta recuperando.
 *
 * Cinque secondi come la verifica di disponibilità: il caso peggiore
 * dell'attesa complessiva resta 15 s, e ha comunque una fine.
 */
export const PROFILE_RELOAD_TIMEOUT_MS = 5_000;

export type UpdateUsernameResponse = {
  msg: string;
  username: string;
  previous_username: string | null;
};

type UsernameAvailabilityResponse = { available: boolean };

type BootstrapProfileResult = {
  profile: ProfileV2 | null;
  session: SessionBootstrapResponse | null;
  companies: CompanyV2[];
  user_applications: JobApplication[];
  selectedCompany: CompanyV2 | null;
  capabilities: SessionBootstrapCapabilities | null;
  next_step: SessionNextStep | null;
  active_subscription_id: string | null;
};

export const mapSessionBootstrapToProfile = (
  context: SessionBootstrapResponse,
): ProfileV2 =>
  ({
    id: context.profile.id,
    email: context.profile.email,
    full_name: context.profile.full_name ?? "",
    username: context.profile.username,
    previous_username: context.profile.previous_username,
    avatar_url: context.profile.avatar_url,
    onboarding_status: context.profile.personal_profile_status,
    personal_profile_status: context.profile.personal_profile_status,
    available_from: context.profile.available_from,
    weekly_hours: context.profile.weekly_hours ?? null,
    last_selected_workspace_id: context.profile.last_selected_workspace_id,
    billing_status: context.profile.billing_status,
    preferred_timezone: null,
    created_at: new Date(),
    updated_at: new Date(),
    profile_type: context.capabilities.can_publish ? "provider" : "consumer",
  }) as ProfileV2;

export const mapSessionBootstrapToResult = (
  context: SessionBootstrapResponse,
  userApplications: JobApplication[] = [],
): BootstrapProfileResult => ({
  profile: mapSessionBootstrapToProfile(context),
  session: context,
  companies: context.publisher_workspaces,
  user_applications: userApplications,
  selectedCompany: context.selected_workspace,
  capabilities: context.capabilities,
  next_step: context.next_step,
  active_subscription_id: context.active_subscription_id,
});

/**
 * Una lettura di sessione fallita chiude la sessione solo se e' il server a
 * dirlo (4xx). Rete caduta, timeout e 5xx non dicono nulla su chi e' l'utente:
 * trattarli come «non autenticato» portava alla pagina di login chiunque avesse
 * il backend irraggiungibile per qualche secondo, perdendo cio' che stava
 * scrivendo. Vale solo se una sessione c'era gia': al primo caricamento non c'e'
 * nulla da conservare e si parte da anonimi, come prima.
 */
export const sessionSurvivesBootstrapError = (
  error: unknown,
  previous: BootstrapProfileResult | undefined,
): previous is BootstrapProfileResult =>
  Boolean(previous?.profile) && !isClientError(error);

const bootstrapProfile = async () => {
  const res: BootstrapProfileResult = {
    profile: null,
    session: null,
    companies: [],
    user_applications: [],
    selectedCompany: null,
    capabilities: null,
    next_step: null,
    active_subscription_id: null,
  };
  const [context, error] = await tryCatch(fetchSessionBootstrap());
  if (error) {
    const previous = queryClient.getQueryData<BootstrapProfileResult>([
      PROFILE_KEY,
      AUTH_KEY,
    ]);
    return sessionSurvivesBootstrapError(error, previous) ? previous : res;
  }

  const [[userApplications, userApplicationsError]] = await Promise.all([
    tryCatch(
      axiosInstance.get<{ job_application: JobApplication[] }>(
        `/job-offer/application/profile`,
      ),
    ),
  ]);

  const mapped = mapSessionBootstrapToResult(
    context,
    userApplicationsError ? [] : userApplications.data.job_application,
  );

  queryClient.setQueryData([BILLING_STATUS_KEY], {
    billing_status: context.profile.billing_status,
    has_open_failures: false,
    last_failure: null,
    grace_period_ends_at: null,
    portal_url_available: true,
  });

  return mapped;
};

export const useBootstrapProfile = () => {
  return useQuery(() => ({
    queryKey: [PROFILE_KEY, AUTH_KEY],
    queryFn: bootstrapProfile,
    staleTime: 5 * 60 * 1000,
  }));
};

/**
 * `/me` restituisce il **proprio** profilo, con le suggerite (BE-REQ R1): per
 * questo il tipo è `OwnProfileV2` e non `ProfileV2`. Ne seguono la query
 * `[PROFILE_KEY]`, `useGetProfileV2` e `readProfileV2Fresh`.
 */
const getProfileV2 = async (signal?: AbortSignal) => {
  const { data } = await axiosInstance.get<OwnProfileV2>("/me", { signal });

  return data;
};

export const createProfileV2QueryOptions = () => {
  return {
    queryKey: [PROFILE_KEY],
    queryFn: () => getProfileV2(),
    staleTime: 0,
  };
};

/**
 * Lo stesso profilo, letto per **uscire da uno stato bloccato** invece che per
 * riempire una pagina. L'unica differenza è il tetto di attesa, che la lettura
 * normale non ha: senza, una risposta che non arriva mai — connessione
 * accettata e poi silenzio, il caso che nessun `catch` intercetta — lascia la
 * card in «sto salvando» per sempre, e da lì l'unica uscita è ricaricare la
 * pagina (AC16).
 *
 * Il segnale della query si combina col tetto, così una rilettura resa inutile
 * (componente smontato) si interrompe comunque.
 *
 * Niente `retry` qui: `fetchQuery` lo mette già a `false` da sé quando non è
 * dichiarato (`queryClient.js`, `fetchQuery`), a differenza di `useQuery` che
 * eredita il default di tre tentativi. Il percorso di recupero passa di lì, e
 * un ritentativo non aggiungerebbe robustezza — moltiplicherebbe il tempo
 * passato nell'unico stato da cui l'utente non può uscire.
 */
export const createProfileV2ReloadQueryOptions = () => {
  return {
    ...createProfileV2QueryOptions(),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getProfileV2(
        AbortSignal.any([
          signal,
          AbortSignal.timeout(PROFILE_RELOAD_TIMEOUT_MS),
        ]),
      ),
  };
};

export const useGetProfileV2 = () => {
  return useQuery(createProfileV2QueryOptions);
};

/**
 * C'è un profilo da mostrare: letto, oppure **riletto male dopo un successo** (P-DL12,
 * S5.8). In TanStack v5 una rilettura fallita mette `status: "error"` e tiene `data`, che
 * è il profilo buono di prima (o quello appena scritto in cache da un salvataggio). Con il
 * solo `isSuccess` la pagina tornerebbe al caricamento, e le skill appena salvate
 * sparirebbero dallo schermo. Il primo caricamento fallito non passa: né successo né
 * rilettura.
 *
 * Solo flag, e **prima** di `data`: solid-query sospende alla prima lettura di `data`
 * mentre la prima lettura è in volo.
 */
export const hasProfileData = (query: {
  isSuccess: boolean;
  isRefetchError: boolean;
}): boolean => query.isSuccess || query.isRefetchError;

/**
 * Il cambio dello username ha un endpoint suo (D2): `PATCH /profile` ignora il
 * campo. Il tetto di attesa è 10 s (D6) e **non** vuol dire fallito — chi
 * chiama ricarica il profilo e guarda com'è andata davvero.
 */
export const updateProfileUsername = async (username: string) => {
  const { data } = await axiosInstance.patch<UpdateUsernameResponse>(
    "/profile/username",
    { username },
    { signal: AbortSignal.timeout(USERNAME_SAVE_TIMEOUT_MS) },
  );
  return data;
};

/**
 * «Libero / non libero» relativo a chi chiede: il proprio username attuale e
 * il proprio precedente risultano liberi (A4). Il tetto di 5 s si combina con
 * il segnale di chi chiama, così una verifica superata da una più recente si
 * interrompe comunque (AC8).
 */
export const checkUsernameAvailability = async (
  value: string,
  signal?: AbortSignal,
) => {
  const timeout = AbortSignal.timeout(USERNAME_CHECK_TIMEOUT_MS);
  const { data } = await axiosInstance.get<UsernameAvailabilityResponse>(
    "/profile/username/availability",
    {
      params: { value },
      signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
    },
  );
  return data.available;
};

/**
 * La stessa `PATCH /profile`, con la risposta dichiarata.
 *
 * Il server **sostituisce** l'insieme delle skill e le restituisce già
 * normalizzate (minuscolo, apostrofi piegati, doppioni tolti): quella lista è
 * l'unica fonte di cosa c'è davvero sul profilo dopo la scrittura, e chi
 * aggiunge skill dalla dashboard la legge per dire quante ne ha aggiunte
 * (EC-FB26). Finora il ritorno era `any`, cioè la stessa informazione senza
 * nessuno che la potesse usare.
 */
export const updateProfileV2 = async (profile: UpdateProfileV2) => {
  const boundedProfile =
    profile.skills === undefined
      ? profile
      : {
          ...profile,
          skills:
            profile.skills === null
              ? null
              : (boundSkillsCsv(profile.skills) ?? ""),
        };
  const { data } = await axiosInstance.patch<UpdateProfileV2Response>(
    "/profile",
    boundedProfile,
  );
  return data;
};

/**
 * `/me` letta **fuori dalla cache**, con il tetto della rilettura (DL14).
 *
 * `fetchQuery` scriverebbe la cache, e una rilettura fatta *prima* di una
 * scrittura muoverebbe le colonne del confronto due volte — una a leggere e una
 * a salvare — mentre le caselle sono bloccate. Qui il valore torna a chi
 * chiede, e la cache si aggiorna solo all'esito.
 */
export const readProfileV2Fresh = () =>
  getProfileV2(AbortSignal.timeout(PROFILE_RELOAD_TIMEOUT_MS));

const suggestedSkillPath = (id: string, action: "confirm" | "reject") =>
  `/v1/profile/suggested-skills/${encodeURIComponent(id)}/${action}`;

/**
 * Conferma una suggerita: da quel momento è una skill **confermata**, non solo
 * dichiarata (S5.1). Nessun body; `id` è l'id del catalogo.
 *
 * Restituisce `void`: la risposta ripete la suggerita decisa, e nessuno la
 * legge, perché le skill del profilo le dice il `PATCH` che viene prima (spec
 * DL12). Niente tetto d'attesa e niente gestione degli errori qui: il
 * salvataggio avvolge la chiamata in `withDeadline` e classifica il rifiuto con
 * `classifySuggestedSkillError` (`./suggested-skills`).
 */
export const confirmSuggestedSkill = async (id: string): Promise<void> => {
  await axiosInstance.post(suggestedSkillPath(id, "confirm"));
};

/**
 * Rifiuta una suggerita, per sempre: non torna più fra le suggerite (S5.1,
 * S4.3). Stesse regole di `confirmSuggestedSkill`.
 */
export const rejectSuggestedSkill = async (id: string): Promise<void> => {
  await axiosInstance.post(suggestedSkillPath(id, "reject"));
};

const updateProfileAvailability = async (
  body: UpdateProfileAvailabilityPayload,
) => {
  const { data } = await axiosInstance.post("/profile/availability", body);
  return data;
};

export const useUpdateProfileV2 = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: updateProfileV2,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

/**
 * Every cache that stops being true when the availability changes.
 *
 * A named function, and not two lines inside `onSuccess`, so the rule can be unit tested
 * without rendering a hook: the second invalidation is the kind that silently stops
 * happening in a refactor, and its absence is invisible until someone opens the
 * dashboard.
 *
 * The dashboard key is one of them because that page **warns** that an unset
 * availability keeps the profile out of every search, and its call to action opens this
 * very modal in place. Without the invalidation the warning that sent the user here is
 * still on screen right after they fixed it — the one moment the page had to show that
 * it worked.
 */
export const invalidateAfterAvailabilityChange = (client: QueryClient) => {
  client.invalidateQueries({ queryKey: [PROFILE_KEY] });
  client.invalidateQueries({ queryKey: [...FREELANCE_DASHBOARD_KEY] });
};

export const useUpdateProfileAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: updateProfileAvailability,
    onSuccess: () => invalidateAfterAvailabilityChange(queryClient),
  }));
};

const updateProfileSocialMedia = async (
  profile: Pick<UpdateProfileV2, "social_media">,
) => {
  const { data } = await axiosInstance.patch("/profile", profile);
  return data;
};

export const useUpdateProfileSocialMedia = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: updateProfileSocialMedia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const getPublicProfileV2 = async (profileId: string) => {
  const { data } = await axiosInstance.get<{ profile: PublicProfileV2 }>(
    `/profile/${profileId}`,
  );
  return data.profile;
};

/** Quanti tentativi valga la pena spendere su una rete inaffidabile. */
const PUBLIC_PROFILE_NETWORK_RETRIES = 2;

/**
 * Query config del profilo pubblico. Estratto dall'hook perché la sheet della
 * ricerca attiva sceglie fra questo endpoint e quello della ricerca in base
 * alla provenienza dell'apertura: il config va condiviso, mai duplicato
 * ad-hoc (drift garantito fra i due rami).
 */
export const createPublicProfileV2QueryConfig = (profileId: string) => ({
  queryKey: [PROFILE_KEY, "public", profileId],
  queryFn: () => getPublicProfileV2(profileId),
  enabled: !!profileId,
  /**
   * Mezzo minuto di freschezza. Serve al redirect dal nome precedente (D13):
   * la rotta semina il profilo che ha già in mano sulla chiave del nome
   * attuale e sostituisce l'URL, e senza questa finestra il montaggio sulla
   * chiave nuova ripartirebbe subito con una seconda richiesta — che per un
   * visitatore anonimo vuol dire due slot del budget per-IP invece di uno,
   * per gli stessi identici byte.
   */
  staleTime: 30_000,
  /**
   * Nessun 4xx viene ritentato.
   *
   * Un 4xx è una **risposta**, non un guasto: ripetere la stessa GET non può
   * cambiarla. Il 404 (id inesistente o profilo non pubblico) e il 400 (id
   * malformato, il link troncato dall'email) ritentati tre volte — il default
   * di TanStack — tengono lo scheletro in piedi per quattro round-trip prima
   * della pagina "non disponibile", lo stato che il FLOW vuole immediato.
   *
   * Il 429 è peggio: ogni ritentativo **consuma un altro slot** del budget
   * anonimo dello stesso IP, quindi il retry allontana il momento in cui la
   * pagina tornerà a funzionare invece di avvicinarlo. Vale per ogni 4xx: anche
   * un 400 ritentato brucia budget senza poter mai riuscire.
   *
   * Tutto il resto (rete caduta, timeout, 5xx) sì: quelli tornano davvero.
   */
  retry: (failureCount: number, error: Error) =>
    !isClientError(error) && failureCount < PUBLIC_PROFILE_NETWORK_RETRIES,
});

/**
 * Accepts an accessor so a long-lived caller can follow a changing id without
 * remounting — a sheet that stays mounted while it plays its exit animation
 * still refetches when the selection changes. A plain string keeps working.
 */
export const useGetPublicProfileV2 = (profileId: string | Accessor<string>) => {
  const id = () => (typeof profileId === "function" ? profileId() : profileId);

  return useQuery(() => createPublicProfileV2QueryConfig(id()));
};

const getAllProfileJobExperience = async (profileId: string) => {
  const { data } = await axiosInstance.get<{ previous_jobs: PreviousJobV2[] }>(
    `/jobs`,
    {
      headers: {
        "x-profile-id": profileId,
      },
    },
  );
  return data;
};

export const createGetAllProfileJobExperienceQueryOptions = (
  profileId: string,
) => {
  return {
    queryKey: [PROFILE_KEY, "job", profileId],
    queryFn: () => getAllProfileJobExperience(profileId),
    select: (data: { previous_jobs: PreviousJobV2[] }) => ({
      ...data,
      previous_jobs: data.previous_jobs.sort((a, b) => {
        return (
          new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
        );
      }),
    }),
  };
};

/**
 * Accetta un accessor come `useGetPublicProfileV2`: la scheda della ricerca
 * attiva cambia freelancer **senza rimontare**, e una stringa letta una volta
 * congelerebbe la `queryKey` sul primo profilo — la storia lavorativa di una
 * persona sotto il nome di un'altra. Una stringa semplice continua a funzionare.
 */
export const useGetAllProfileJobExperienceV2 = (
  profileId: string | Accessor<string>,
) => {
  const id = () => (typeof profileId === "function" ? profileId() : profileId);
  return useQuery(() => ({
    ...createGetAllProfileJobExperienceQueryOptions(id()),
    enabled: !!id(),
  }));
};

const getJobExperience = async ({
  previousJobId,
  profileId,
}: {
  previousJobId: string;
  profileId: string;
}) => {
  const { data } = await axiosInstance.get<{ previous_job: PreviousJobV2 }>(
    `/jobs/${previousJobId}`,
    {
      headers: {
        "x-profile-id": profileId,
      },
    },
  );
  return data;
};

export const useGetJobExperienceV2 = ({
  previousJobId,
  profileId,
}: {
  previousJobId: string;
  profileId: string;
}) => {
  return useQuery(() => ({
    queryKey: [PROFILE_KEY, "job", profileId, previousJobId],
    queryFn: () => getJobExperience({ previousJobId, profileId }),
    enabled: !!profileId && !!previousJobId,
  }));
};

const createJobExperience = async (body: PreviousJobInsertV2) => {
  const { data } = await axiosInstance.post("/jobs", body);
  return data;
};

export const useCreateJobExperienceV2 = () => {
  return useMutation(() => ({
    mutationFn: createJobExperience,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const updateJobExperience = async (id: string, body: UpdateJobExperience) => {
  const { data } = await axiosInstance.patch(`/jobs/${id}`, {
    job: body,
  });
  return data;
};

export const useUpdateJobExperienceV2 = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: UpdateJobExperience) => updateJobExperience(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const deleteJobExperience = async (id: string) => {
  const { data } = await axiosInstance.delete(`/jobs/${id}`);
  return data;
};

export const useDeleteJobExperienceV2 = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: deleteJobExperience,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const getAllProfileEducation = async (profileId: string) => {
  const { data } = await axiosInstance.get<{ education: EducationV2[] }>(
    `/education`,
    {
      headers: {
        "x-profile-id": profileId,
      },
    },
  );
  return data;
};

/** Accessor per lo stesso motivo di `useGetAllProfileJobExperienceV2`. */
export const useGetAllProfileEducationV2 = (
  profileId: string | Accessor<string>,
) => {
  const id = () => (typeof profileId === "function" ? profileId() : profileId);
  return useQuery(() => ({
    queryKey: [PROFILE_KEY, "education", id()],
    queryFn: () => getAllProfileEducation(id()),
    select: (data) => ({
      ...data,
      education: data.education.sort((a, b) => {
        return (
          new Date(b.start_date!).getTime() - new Date(a.start_date!).getTime()
        );
      }),
    }),
    enabled: !!id(),
  }));
};

export const getEducation = async ({
  previousEducationId,
  profileId,
}: {
  previousEducationId: string;
  profileId: string;
}) => {
  const { data } = await axiosInstance.get<{ education: EducationV2 }>(
    `/education/${previousEducationId}`,
    {
      headers: {
        "x-profile-id": profileId,
      },
    },
  );
  return data;
};

export const useGetEducationV2 = ({
  previousEducationId,
  profileId,
}: {
  previousEducationId: string;
  profileId: string;
}) => {
  return useQuery(() => ({
    queryKey: [PROFILE_KEY, "education", previousEducationId],
    queryFn: () => getEducation({ previousEducationId, profileId }),
    enabled: !!previousEducationId && !!profileId,
    staleTime: 0,
  }));
};

const createEducation = async (body: EducationInsertV2) => {
  const { data } = await axiosInstance.post("/education", body);
  return data;
};

export const useCreateEducationV2 = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: createEducation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const deleteEducation = async (id: string) => {
  const { data } = await axiosInstance.delete(`/education/${id}`);
  return data;
};

export const useDeleteEducationV2 = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: deleteEducation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const updateEducation = async (id: string, body: UpdateEducation) => {
  const { data } = await axiosInstance.patch(`/education/${id}`, {
    education: body,
  });
  return data;
};

export const useUpdateEducationV2 = (id: string) => {
  return useMutation(() => ({
    mutationFn: (body: UpdateEducation) => updateEducation(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const updateProfileImage = async (payload: UpdateProfileImagePayload) => {
  const formData = new FormData();
  formData.append("image", payload.image);
  const { data } = await axiosInstance.post<{
    profile: ProfileV2;
  }>("/profile/image", formData);
  return data;
};

export const useUpdateProfileImage = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: updateProfileImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

type UploadResumePayload = {
  resume: File;
};

export const uploadUserResume = async (payload: UploadResumePayload) => {
  const formData = new FormData();
  formData.append("resume", payload.resume);
  const { data } = await axiosInstance.post<{ asset: Asset }>(
    "/profile/resume",
    formData,
  );
  return data;
};

export const useUploadUserResume = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: uploadUserResume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const deleteUserResume = async (id: string) => {
  const { data } = await axiosInstance.delete(`/profile/resume/${id}`);
  return data;
};

export const useDeleteUserResume = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: deleteUserResume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const patchDefaultResume = async (assetId: string) => {
  await axiosInstance.patch(`/profile/assets/${assetId}/default`);
};

export const useSetDefaultResume = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: patchDefaultResume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

type NewsletterOptOutResponse = {
  msg: string;
};

type NewsletterOptInResponse = {
  msg: string;
};

const optOutNewsletter = async () => {
  const { data } = await axiosInstance.post<NewsletterOptOutResponse>(
    "/profile/newsletter/opt-out",
  );
  return data;
};

const optInNewsletter = async () => {
  const { data } = await axiosInstance.post<NewsletterOptInResponse>(
    "/profile/newsletter/opt-in",
  );
  return data;
};

export const useOptOutNewsletter = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: optOutNewsletter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

export const useOptInNewsletter = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: optInNewsletter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

// LinkedIn Sync API

type LinkedInStatusResponse = {
  msg: string;
  data: {
    has_connection: boolean;
    linkedin_url: string | null;
    linkedin_public_identifier: string | null;
    last_sync_at: string | null;
  };
};

type LinkedInRefreshPayload = {
  linkedin_url?: string;
};

type LinkedInRefreshResponse = {
  msg: string;
  data: {
    linkedin_url: string;
    linkedin_public_identifier: string;
    last_sync_at: string;
  };
};

type LinkedInDisconnectResponse = {
  msg: string;
};

const getLinkedInStatus = async () => {
  const { data } = await axiosInstance.get<LinkedInStatusResponse>(
    "/profile/linkedin/status",
  );
  return data.data;
};

export const useGetLinkedInStatus = () => {
  return useQuery(() => ({
    queryKey: [PROFILE_KEY, "linkedin", "status"],
    queryFn: getLinkedInStatus,
  }));
};

const refreshLinkedInProfile = async (
  payload?: LinkedInRefreshPayload,
): Promise<LinkedInRefreshResponse["data"]> => {
  const { data } = await axiosInstance.post<LinkedInRefreshResponse>(
    "/profile/linkedin/refresh",
    payload ?? {},
  );
  return data.data;
};

export const useRefreshLinkedInProfile = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: refreshLinkedInProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY, "linkedin"] });
    },
  }));
};

const disconnectLinkedIn = async () => {
  const { data } = await axiosInstance.delete<LinkedInDisconnectResponse>(
    "/profile/linkedin/disconnect",
  );
  return data;
};

export const useDisconnectLinkedIn = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: disconnectLinkedIn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY, "linkedin"] });
    },
  }));
};
